/**
 * Tenant context and hooks. The provider lives in `TenantProvider.tsx`.
 *
 *   const tenant = useTenant();            // Tenant (always set; see below)
 *   const hasCareers = useFeature("careers");
 *   const { phase, refresh } = useTenantState();
 *
 * Outside the "ready" and "paused" phases `useTenant()` returns a neutral
 * fallback (display name "TutorLoop", every feature off) so screens such as
 * the not-found page can still call it safely.
 */

import { createContext, useContext } from "react";

import type { FeatureName, Tenant } from "@/lib/api/tenant";

export type TenantPhase = "loading" | "ready" | "paused" | "not_found" | "error";

export interface TenantContextValue {
  tenant: Tenant;
  phase: TenantPhase;
  /** Refetch `GET /tenant/` (after branding or plan changes). */
  refresh: () => Promise<void>;
}

export const TenantContext = createContext<TenantContextValue | null>(null);

function useCtx(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used inside <TenantProvider>");
  return ctx;
}

export function useTenant(): Tenant {
  return useCtx().tenant;
}

export function useTenantState(): { phase: TenantPhase; refresh: () => Promise<void> } {
  const { phase, refresh } = useCtx();
  return { phase, refresh };
}

/** True when the tenant has the feature switched on. */
export function useFeature(name: FeatureName): boolean {
  return useCtx().tenant.features[name];
}
